import { useEffect, useRef, useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import type { ISpaceAuthLookupResponse } from "@/api/space/dtos/authenticator.dto";
import { keys_authenticator } from "@/api/space/hooks/useSpace.authenticator";
import { definirDoisFatoresPendente } from "@/api/space/estadoAutenticacao.space";
import { SPACE_BYPASS_2FA } from "@/configs/runtime";
import useAutenticacaoJwt from "../../../../useAutenticacaoJwt";
import { inferIsSpaceUser } from "../../../../utils/utilitariosJwt";
import { deveIgnorarDoisFatoresParaUsuarioAutenticado, deveUsarFluxoOtpDireto } from "../utils/loginGrupoFixo.utils";
import type { FormularioEntrarType } from "../loginGrupoFixo.schema";
import type { DadosPendentesDoisFatores } from "../loginGrupoFixo.types";

// --------------------------------- TYPES ---------------------------------
type UseFluxoLoginGrupoFixoOptions = {
	readonly groupId: string;
	readonly aoErro: (campo: "username" | "password" | "root", mensagem: string) => void;
};

// ------------------------------- FUNCTIONS -------------------------------
export default function useFluxoLoginGrupoFixo({ groupId, aoErro }: Readonly<UseFluxoLoginGrupoFixoOptions>) {
	// -------------------------------- STATES --------------------------------
	const [pendente, setPendente] = useState<DadosPendentesDoisFatores | null>(null);
	const enviandoRef = useRef(false);
	const montadoRef = useRef(false);
	const grupoAtualRef = useRef(groupId);
	grupoAtualRef.current = groupId;
	const { entrar: autenticar, concluirEntrarDoisFatores, sair } = useAutenticacaoJwt();

	// -------------------------------- QUERIES -------------------------------
	const isSigningIn = useIsMutating({ mutationKey: keys_authenticator.login() }) > 0;

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => {
		montadoRef.current = true;
		return () => {
			montadoRef.current = false;
		};
	}, []);

	// -------------------------------- HANDLERS ------------------------------
	function abrirOtp(dados: DadosPendentesDoisFatores): void {
		sair?.();
		definirDoisFatoresPendente(true);
		setPendente(dados);
	}

	function concluirOtp(): void {
		definirDoisFatoresPendente(false);
		setPendente(null);
	}

	function cancelarOtp(): void {
		concluirOtp();
		sair?.();
	}

	async function entrarPorSenha(
		dados: FormularioEntrarType,
		usuario: ISpaceAuthLookupResponse | null,
	): Promise<void> {
		if (!dados.password?.trim()) return aoErro("password", "A senha deve ter pelo menos 1 caractere.");
		const resultado = await autenticar?.({
			username: dados.username,
			password: dados.password,
			groupId,
			id_cad_pessoa: usuario?.id_cad_pessoa ?? undefined,
		});
		if (!montadoRef.current || grupoAtualRef.current !== groupId || resultado?.status !== "2fa_required") return;
		const space = inferIsSpaceUser({ username: usuario?.nome });
		const bypass =
			(Boolean(usuario?.autenticacao_dois_fatores) && space && SPACE_BYPASS_2FA) ||
			deveIgnorarDoisFatoresParaUsuarioAutenticado(resultado.user, SPACE_BYPASS_2FA);
		if (bypass) {
			concluirEntrarDoisFatores?.({
				user: resultado.user,
				access_token: resultado.access_token,
				groupId: resultado.groupId,
			});
			concluirOtp();
			return;
		}
		if (!resultado.username?.trim() || resultado.id_cad_pessoa_funcionario == null)
			return aoErro("root", "Dados insuficientes para autenticação 2FA.");
		abrirOtp({
			groupId: resultado.groupId,
			username: resultado.username.trim(),
			id_cad_pessoa_funcionario: resultado.id_cad_pessoa_funcionario,
			showUsernameInput: space,
			eh_usuario_space: space,
			preAuthUser: resultado.user,
		});
	}

	function registrarErros(error: unknown): void {
		if (typeof error !== "object" || error === null || !("data" in error) || !Array.isArray(error.data)) return;
		for (const item of error.data) {
			if (item === null || typeof item !== "object" || typeof item.message !== "string") continue;
			if (item.type === "username" || item.type === "password" || item.type === "root")
				aoErro(item.type, item.message);
		}
	}

	async function entrar(dados: FormularioEntrarType, usuario: ISpaceAuthLookupResponse | null): Promise<void> {
		if (enviandoRef.current || pendente) return;
		if (!groupId || groupId !== grupoAtualRef.current) return aoErro("root", "id-grupo (base) não configurado.");
		if (deveUsarFluxoOtpDireto(usuario, SPACE_BYPASS_2FA)) {
			const nome = (usuario?.nome ?? dados.username).trim();
			if (!nome) return aoErro("username", "Selecione um usuário válido.");
			if (usuario?.id_cad_pessoa_funcionario == null)
				return aoErro("root", "Usuário sem id_cad_pessoa_funcionario.");
			const space = inferIsSpaceUser({ username: usuario.nome });
			abrirOtp({
				groupId,
				username: nome,
				id_cad_pessoa_funcionario: usuario.id_cad_pessoa_funcionario,
				showUsernameInput: space,
				eh_usuario_space: space,
			});
			return;
		}
		enviandoRef.current = true;
		try {
			await entrarPorSenha(dados, usuario);
		} catch (error: unknown) {
			if (montadoRef.current) registrarErros(error);
		} finally {
			enviandoRef.current = false;
		}
	}

	// -------------------------------- RETURN --------------------------------
	return { pendente, isSigningIn, entrar, cancelarOtp, concluirOtp };
}
